import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { validateTodoInput, sanitizeInput } from '@/lib/utils';
import { toast } from 'sonner';
import TodoDurationModal from './TodoDurationModal';
import { Mission, MissionSize } from '@/types/todo';

interface AddTodoFormProps {
  onAdd: (title: string, durationMinutes?: number, missionId?: string) => void;
  onOpenSettings?: (defaultSection?: string) => void;
  missions?: Mission[];
  showMissions?: boolean;
  onCreateMission?: (name: string, size: MissionSize) => { id: string; mission: Mission };
}
export default function AddTodoForm({
  onAdd,
  onOpenSettings,
  missions = [],
  showMissions = false,
  onCreateMission
}: AddTodoFormProps) {
  const [title, setTitle] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [pendingTitle, setPendingTitle] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = validateTodoInput(title);
    if (!validation.isValid) {
      toast.error(validation.error || "Invalid input");
      return;
    }
    
    const sanitizedTitle = sanitizeInput(title.trim());
    if (sanitizedTitle) {
      setPendingTitle(sanitizedTitle);
      setShowModal(true);
      setTitle('');
    }
  };

  const handleModalConfirm = (title: string, durationMinutes?: number, missionId?: string) => {
    onAdd(title, durationMinutes, missionId);
    setShowModal(false);
    setPendingTitle('');
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setPendingTitle('');
  };
  return (
    <>
      <form onSubmit={handleSubmit} className="mb-6">
        <div className="relative">
          <Input 
            type="text" 
            value={title} 
            onChange={e => setTitle(e.target.value)} 
            placeholder="Add a new todo..." 
            style={{
              fontSize: '16px'
            }} // Prevent zoom on iOS
            className="text-base bg-card dark:bg-muted border-input dark:border-muted-foreground/20 text-foreground pl-6 pr-16 rounded-full h-12" 
          />
          <Button 
            type="submit" 
            size="sm"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0" 
            disabled={!title.trim()}
            aria-label="Add todo"
          >
            <Plus size={16} />
          </Button>
        </div>
      </form>

      <TodoDurationModal
        isOpen={showModal}
        initialTitle={pendingTitle}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
        onOpenSettings={onOpenSettings}
        missions={missions}
        showMissions={showMissions}
        onCreateMission={onCreateMission}
      />
    </>
  );
}